import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingCart, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const SERVICE_LINKS = [
  { label: "Service & Maintenance", target: "/maintenance" },
  { label: "Saltwater Aquariums", target: "/saltwater" },
  { label: "Freshwater Aquariums", target: "/freshwater" },
  { label: "Reef Aquariums", target: "/reef" },
  { label: "24/7 Emergency Services", target: "/emergency" },
  { label: "Aquarium Water Delivery", target: "/water-delivery" },
  { label: "Aquarium Design & Installation", target: "/design" },
  { label: "6-Month Guarantee", target: "/guarantee" },
  { label: "View All Services", target: "/services" },
];

const LINKS = [
  { label: "Home", target: "#home" },
  { label: "About", target: "#products" },
  { label: "Services", target: "/services", submenu: SERVICE_LINKS },
  { label: "Gallery", target: "#services" },
  { label: "Testimonials", target: "#testimonials" },
  { label: "Contact", target: "#contact" },
];

export const scrollToTarget = (target) => {
  if (target === "bottom") {
    const y = document.body.scrollHeight;
    if (window.__lenis) window.__lenis.scrollTo(y, { duration: 1.4 });
    else window.scrollTo({ top: y, behavior: "smooth" });
    return;
  }
  const el = document.querySelector(target);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const scrollTo = scrollToTarget;

const isAppleWebkit =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent)));

const Logo = ({ className = "" }) => {
  if (isAppleWebkit) {
    return (
      <img
        className={`h-20 lg:h-24 w-auto ${className}`}
        src="/media/seatech-logo2-transparent.png"
        alt="Seatech Aquariums"
        draggable={false}
      />
    );
  }
  return (
    <video
      className={`h-20 lg:h-24 w-auto ${className}`}
      src="/media/seatech-logo2.webm"
      poster="/media/seatech-logo2-transparent.png"
      autoPlay
      loop
      muted
      playsInline
    />
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (target) => {
    setOpen(false);
    setMobileSubOpen(false);
    if (target.startsWith("/")) {
      navigate(target);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTarget: target } });
    } else {
      scrollToTarget(target);
    }
  };

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 left-0 right-0 z-50 px-4 lg:px-8 pt-4 lg:pt-6"
      >
        <nav
          className="mx-auto max-w-[1640px] rounded-2xl px-6 lg:px-12 flex items-center justify-between h-24 lg:h-28 bg-[#0b1024]/85 backdrop-blur-md"
        >
          <button
            onClick={() => go("#home")}
            data-testid="nav-logo"
            className="flex items-center outline-none shrink-0"
            aria-label="Seatech home"
          >
            <Logo />
          </button>

          <ul className="hidden lg:flex items-center gap-8 xl:gap-10 lg:-translate-x-4 xl:-translate-x-8">
            {LINKS.map((l) => (
              <li key={l.label} className="relative group">
                <button
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.target)}
                  className="flex items-center gap-1.5 font-body text-[0.9rem] font-medium tracking-[0.18em] uppercase text-[var(--foam)]/90 hover:text-[var(--aqua)] transition-colors duration-300"
                >
                  {l.label}
                  {l.submenu && <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />}
                </button>

                {l.submenu && (
                  <div className="invisible absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                    <div className="overflow-hidden rounded-2xl border border-[var(--foam)]/10 bg-[#0b1024]/95 p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl">
                      {l.submenu.map((s) => (
                        <button
                          key={s.label}
                          data-testid={`nav-sublink-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                          onClick={() => go(s.target)}
                          className="block w-full rounded-xl px-4 py-2.5 text-left font-body text-[0.82rem] font-light tracking-[0.1em] text-[var(--foam)]/85 transition-colors duration-200 hover:bg-[var(--aqua)]/10 hover:text-[var(--aqua)]"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center gap-7 shrink-0">
            <button
              data-testid="nav-login"
              onClick={() => go("#contact")}
              className="font-body text-[0.95rem] text-[var(--foam)]/90 hover:text-[var(--aqua)] transition-colors"
            >
              Log In
            </button>
            <button data-testid="nav-cart" onClick={() => go("/water-delivery")} className="relative text-[var(--foam)]/90 hover:text-[var(--aqua)] transition-colors" aria-label="Order Water Delivery">
              <ShoppingCart size={24} strokeWidth={1.6} />
              <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-[var(--foam)] text-[var(--ink-900)] text-[10px] font-semibold flex items-center justify-center">0</span>
            </button>
          </div>

          <button
            className="lg:hidden text-[var(--foam)]"
            onClick={() => { setOpen((v) => !v); setMobileSubOpen(false); }}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-[var(--ink-900)]/96 backdrop-blur-xl flex flex-col items-center gap-5 overflow-y-auto px-6 pt-44 pb-14"
          >
            {LINKS.map((l, i) => (
              <div key={l.label} className="flex flex-col items-center gap-3">
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => (l.submenu ? setMobileSubOpen((v) => !v) : go(l.target))}
                  className="flex items-center gap-2 font-title text-2xl uppercase text-[var(--foam)]"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                  {l.submenu && (
                    <ChevronDown
                      size={20}
                      className={`transition-transform duration-300 ${mobileSubOpen ? "rotate-180" : ""}`}
                    />
                  )}
                </motion.button>
                {l.submenu && mobileSubOpen && (
                  <div className="flex flex-col items-center gap-2.5" data-testid="mobile-services-submenu">
                    {l.submenu.map((s) => (
                      <button
                        key={s.label}
                        onClick={() => go(s.target)}
                        data-testid={`mobile-sublink-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        className="font-body text-sm font-light tracking-[0.1em] text-[var(--foam)]/70"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button onClick={() => go("#contact")} className="mt-4 font-body text-[var(--foam)]/80" data-testid="mobile-login">Log In</button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
